<template>
  <section class="home-hero-item">
    <div class="home-hero-item__inner">
      <div
          style="opacity: 0;"
          class="home-hero-item__content">
        <AppTagText v-if="computedTag?.item" :title="computedTag.item.title"/>
        <h2 class="home-hero-item__title"
            v-html="computedTitle"/>
        <p v-if="data.sub_title"
            class="home-hero-item__text">{{
            data.sub_title
          }}</p>
        <AppButton :url="pageOrUrl" :title="computedLabel" :end-icon="true" :aria-label="`${computedTag?.item?.title ? sanitizeMarkedHeading(computedTag?.item?.title) : ''} ${computedTitle} ${data?.sub_title || ''} ${computedLabel}`" />
      </div>
    </div>

    <AppImageFocal
        v-if="data.image"
        class="home-hero-item__image"
        :data="data.image"
        :eager="(props.index < 1)"
        :renditions="renditions"
    />
    <div class="home-hero-item__wrapper">
      <LazyAppStreamingVideo
          v-if="data.hls && data.mp4"
          :hls="data.hls"
          :mp4="data.mp4"
          :loop="true"
          :interactive="false"
          :auto-play="true"
          :muted="true"
          :content-fit="'cover'"
          class="home-hero-item__streaming-video"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import {TCmsHeroItem} from "~/types/_cms-types";
import {computed} from "vue";
import {joinSlugId} from "~/utils/slug-id";
import {sanitizeMarkedHeading} from "~/utils/data-transformers";

const renditions = [
  {width: 575, height: 944, breakpoint: 'xs'},
  {width: 767, height: 419, breakpoint: 'sm'},
  {width: 991, height: 823, breakpoint: 'md'},
  {width: 1440, height: 693, breakpoint: 'lg'}
]

const props = defineProps<{
  data: TCmsHeroItem,
  index: number
}>()

const computedTitle = computed(() => {
  return props.data.title || props.data.page[0].item.title
})

const computedTag = computed(() => {
  if (props.data.belongs_to[0]?.item) {
    return props.data.belongs_to[0]
  }
  if (props.data.page[0] && props.data.page[0].item?.belongs_to) {
    return props.data.page[0].item.belongs_to[0]
  }
  return undefined
})

const computedLabel = computed(() => {
  return props.data.button_label || 'Bekijk'
})

const pageOrUrl = computed(() => {
  const page = props.data.page[0]?.item
  const collection = props.data.page[0]?.collection

  if (page && collection) {
    const pageType = collection.split('_page')[0]
    const slugId = joinSlugId(page)
    if (pageType === 'content') {
      if (page.belongs_to?.slug) {
        return `/${pageType}-${page.belongs_to?.slug}/${slugId}/`
      } else {
        return `/${slugId}/`
      }
    }
    if (pageType === 'articles') {
      return `/archive/${slugId}/`
    } else {
      return `/${pageType}/${slugId}/`
    }
  } else {
    return props.data.url
  }
})

</script>

<style scoped lang="postcss">
.home-hero-item {
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  @mixin app-max-header-height;
  aspect-ratio: 575 / 944;

  &__inner {
    z-index: 20;
    grid-column: 1 / -1;
    grid-row-start: 1;
    padding-bottom: 3.5rem;
    @mixin app-layout-container;

    @media (--md) {
      padding-bottom: 70px;
    }

    @media (--xl) {
      padding-bottom: 5rem;
    }
  }

  &__content {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 0.875rem;

    @media (--xl) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }
  }

  &__title {
    hyphens: manual;
    font-family: Midnight, sans-serif;
    font-size: 28px;
    text-transform: uppercase;
    line-height: 0.9;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;

    @media (--md) {
      font-size: 40px;
      line-height: 0.9;
    }

    @media (--xl) {
      font-size: 80px;
      line-height: 0.8;
    }
  }

  &__text {
    color: #fff;
    @mixin app-intro-mobile;
  }

  &__image {
    position: relative;
    grid-column: 1 / -1;
    grid-row-start: 1;
    @mixin app-max-header-height;
  }

  &__wrapper {
    position: relative;
    z-index: 10;
    grid-column: 1 / -1;
    grid-row-start: 1;
  }

  &__streaming-video {
    position: absolute;
    left: 0px;
    top: 0px;
    height: 100%;
    width: 100%;
  }

  @media (--sm) {
    aspect-ratio: 767 / 419;
  }

  @media (--md) {
    aspect-ratio: 991 / 823;
  }

  @media (--lg) {
    aspect-ratio: 1200 / 578;
  }

  @media (--xl) {
    aspect-ratio: 1920 / 924;
  }

  &::after {
    position: absolute;
    left: 0px;
    top: 0px;
    z-index: 10;
    height: 100%;
    width: 100%;
    content: "";
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 35%, rgba(0, 0, 0, 0.5) 100%);
  }
}
</style>
